"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Tells whether a `line-clamp`-ed element is actually truncated, so a
 * "show more" toggle can be hidden when the whole text already fits.
 *
 * Measuring only makes sense while the element is clamped, hence `enabled`:
 * once the text is expanded the last known value is kept.
 */
export const useIsTextClamped = <T extends HTMLElement>(
  text?: string,
  enabled = true,
) => {
  const ref = useRef<T>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!(element && enabled)) {
      return;
    }

    const measure = () => {
      setIsClamped(
        element.scrollHeight > element.clientHeight ||
          element.scrollWidth > element.clientWidth,
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [text, enabled]);

  return { ref, isClamped };
};
